import { HelpCircle, Video } from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { HelpPopup } from "@/features/knowledge/components/help-popup";
import { HelpVideosPopup } from "@/features/knowledge/components/help-videos-popup";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { cn } from "@/lib/utils";

const baseTabs = [
  { label: "Home", path: "/kb", end: true },
  { label: "FAQ", path: "/kb/faq", end: true },
  { label: "What's New", path: "/kb/whats-new", end: false },
  { label: "Library", path: "/kb/library", end: true },
  { label: "Toolbox", path: "/kb/toolbox", end: false },
  { label: "Videos", path: "/kb/videos", end: false },
];

const adminTabs = [
  { label: "Manage FAQ", path: "/kb/faq/manage", end: false },
  { label: "Manage Wiki", path: "/kb/wiki/manage", end: false },
];

/**
 * Shell for the Knowledge Base (`/kb/*`). Firm-visible browsing tabs plus, for
 * platform admins, the authoring tabs. The "?" affordance opens contextual help
 * for the current screen (a `HelpPopup`), reachable from anywhere in the KB.
 */
export function KbLayout() {
  const isPlatformAdmin = useAuthStore((s) => s.user?.is_platform_admin ?? false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [videosOpen, setVideosOpen] = useState(false);
  const tabs = isPlatformAdmin ? [...baseTabs, ...adminTabs] : baseTabs;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Knowledge Base</h1>
          <p className="text-muted-foreground text-sm">
            FAQs, wiki articles, resource library, and how-to videos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setVideosOpen(true)}>
            <Video className="size-4" />
            Help videos
          </Button>
          <Button variant="outline" size="sm" onClick={() => setHelpOpen(true)}>
            <HelpCircle className="size-4" />
            Help
          </Button>
        </div>
      </div>
      <div className="bg-card -mx-6 min-w-0 border-b px-6 py-2.5">
        <div className="flex flex-wrap items-center gap-1">
          {tabs.map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              end={tab.end}
              className={({ isActive }) =>
                cn(
                  "flex h-9 shrink-0 items-center rounded-[10px] px-3.5 text-[13px] font-medium transition-colors",
                  isActive
                    ? "bg-brand-panel font-semibold text-white"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted",
                )
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </div>
      </div>
      <div>
        <Outlet />
      </div>
      <HelpPopup nodeKey="knowledge-base" open={helpOpen} onOpenChange={setHelpOpen} />
      <HelpVideosPopup screenSlug="tooltips" open={videosOpen} onOpenChange={setVideosOpen} />
    </div>
  );
}
